import React, { useMemo, useState } from 'react';
import { Search, FileText, Eye, FolderOpen, Settings2 } from 'lucide-react';
import { Button, Select } from '../../../components';
import { Badge, Card, EmptyState, formatDate, inputClass, policyStatus, policyFormatLabel } from './shared';

/**
 * The policy library.
 *
 * Its whole job is making the CURRENT policy easy to find and hard to confuse
 * with anything else. Hence the four tabs: what is in force, what needs
 * reviewing soon, what is still being drafted, and what has been retired. A
 * policy that has a published version AND a draft in progress shows both
 * badges, because "there's a new one coming" and "this is the one you follow
 * today" are different facts and staff need the second one.
 *
 * Categories are folders for finding things, not a compliance taxonomy, and
 * the sidebar says so.
 */
const PolicyLibrary = ({ policies, summary, categories, onOpen, onNew, onManageCategories }) => {
  const [tab, setTab] = useState('all');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return policies
      .filter((p) => (tab === 'archived' ? p.archived : !p.archived))
      .filter((p) => {
        if (tab === 'drafts') return p.has_draft || p.state === 'draft';
        if (tab === 'review') return ['review_due', 'review_overdue'].includes(p.state);
        return true;
      })
      .filter((p) => category === 'all' || p.category === category)
      .filter((p) => !needle || `${p.title} ${p.category} ${p.owner_name || ''}`.toLowerCase().includes(needle))
      .sort((a, b) => String(a.title).localeCompare(String(b.title)));
  }, [policies, tab, category, query]);

  // Sidebar counts follow the archived/active split of the tab you are on, so
  // the numbers always add up to what the list can actually show.
  const scope = useMemo(
    () => policies.filter((p) => (tab === 'archived' ? p.archived : !p.archived)),
    [policies, tab],
  );

  const tabs = [
    ['all', 'All policies', summary?.all ?? 0],
    ['review', 'Review due', summary?.review ?? 0],
    ['drafts', 'Drafts', summary?.drafts ?? 0],
    ['archived', 'Archived', summary?.archived ?? 0],
  ];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[215px_minmax(0,1fr)] gap-5 items-start">
      {/* Category folders. Hidden on narrow screens, where the toolbar's
          select does the same job without stealing a third of the width. */}
      <aside className="hidden xl:block bg-[#F8FAFD] dark:bg-gray-800/30 border border-[#E3E9F3] dark:border-gray-800 rounded-[16px] p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">Categories</h3>
          <Button size="sm" variant="ghost" onClick={onManageCategories}>Manage</Button>
        </div>
        <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1.5">Find policies by subject.</p>

        <nav className="flex flex-col gap-1 mt-4" aria-label="Policy categories">
          <CategoryButton
            active={category === 'all'}
            label="All policies"
            count={scope.length}
            onClick={() => setCategory('all')}
          />
          {categories.map((name) => (
            <CategoryButton
              key={name}
              active={category === name}
              label={name}
              count={scope.filter((p) => p.category === name).length}
              onClick={() => setCategory(name)}
            />
          ))}
        </nav>

        <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-4 leading-relaxed">
          Counts include {tab === 'archived' ? 'archived' : 'all non-archived'} policies.
        </p>
      </aside>

      <Card className="min-w-0">
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 flex-wrap">
          <div>
            <h2 className="text-[17px] font-bold text-[#0B1220] dark:text-gray-100">Policy library</h2>
            <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1.5">
              Keep the current policy easy to find, read and review.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="xl:hidden"
            leftIcon={<Settings2 className="w-4 h-4" />}
            onClick={onManageCategories}
          >
            Categories
          </Button>
        </div>

        <div className="flex gap-6 px-6 border-b border-[#E3E9F3] dark:border-gray-800 overflow-x-auto">
          {tabs.map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              aria-pressed={tab === value}
              onClick={() => { setTab(value); setCategory('all'); }}
              className={`py-3.5 text-[13px] whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === value
                  ? 'border-blue-600 text-blue-600 font-semibold'
                  : 'border-transparent text-[#64748B] dark:text-gray-400 hover:text-[#0B1220] dark:hover:text-gray-200'
              }`}
            >
              {label}
              <span className="ml-1.5 text-[11px] text-[#94A3B8] dark:text-gray-500 tabular-nums">{count}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5 flex-wrap px-6 py-5">
          <div className="relative flex-1 min-w-[180px] max-w-[420px]">
            <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              className={`${inputClass} pl-9 py-2`}
              type="search"
              placeholder="Search policies…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search policies"
            />
          </div>
          <Select
            value={category}
            onChange={setCategory}
            size="sm"
            className="w-[220px] xl:hidden"
            options={[{ value: 'all', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
          />
          <span className="text-[11px] text-[#94A3B8] dark:text-gray-500 ml-auto hidden sm:block">
            Review due includes the next 30 days.
          </span>
        </div>

        {!visible.length ? (
          <EmptyState
            icon={FolderOpen}
            title="No policies found"
            description={emptyMessage(tab, query, category)}
            action={query || category !== 'all'
              ? <Button variant="secondary" onClick={() => { setQuery(''); setCategory('all'); }}>Clear filters</Button>
              : ['all', 'drafts'].includes(tab)
                ? <Button variant="primary" onClick={onNew}>Add policy</Button>
                : null}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-t border-[#E3E9F3] dark:border-gray-800">
                    {['Policy', 'Owner', 'Review date', 'Status', ''].map((heading, i) => (
                      <th
                        key={heading || i}
                        className={`bg-[#F8FAFD] dark:bg-gray-800/40 text-[11px] font-semibold text-[#8391A7] dark:text-gray-400 text-left py-3.5 px-5 ${i === 0 ? 'pl-6' : ''} ${i === 4 ? 'pr-6' : ''}`}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((policy) => {
                    const state = policyStatus(policy);
                    const showing = policy.current || policy.draft;
                    const overdue = policy.state === 'review_overdue';
                    return (
                      <tr key={policy.id} className="border-t border-[#EEF2F8] dark:border-gray-800 hover:bg-[#FCFDFF] dark:hover:bg-gray-800/30">
                        <td className="py-5 px-5 pl-6 align-top">
                          <div className="flex items-start gap-3">
                            <span className="w-9 h-10 rounded-[10px] bg-[#EEF3FF] dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                              <FileText className="w-4 h-4 text-blue-600" />
                            </span>
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => onOpen(policy.id)}
                                className="text-[13px] font-bold text-[#121B2D] dark:text-gray-100 hover:text-blue-600 text-left leading-snug"
                              >
                                {policy.title}
                              </button>
                              <div className="text-[11px] text-[#8090AA] dark:text-gray-500 mt-1.5">
                                {policy.category}
                                <span className="mx-1.5">·</span>
                                {policyFormatLabel(showing)}
                                <span className="mx-1.5">·</span>
                                {policy.current ? `Version ${policy.current.version}` : 'Unpublished'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-5 px-5 align-top text-[12.5px] text-[#50627D] dark:text-gray-300">
                          {policy.owner_name || '—'}
                        </td>
                        <td className="py-5 px-5 align-top text-[12.5px]">
                          <span className={overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-[#50627D] dark:text-gray-300'}>
                            {formatDate(showing?.review_date)}
                          </span>
                          {overdue && <div className="text-[11px] text-rose-600 dark:text-rose-400 mt-1">Review overdue</div>}
                        </td>
                        <td className="py-5 px-5 align-top">
                          <div className="flex flex-wrap gap-1.5">
                            <Badge tone={state.tone}>{state.label}</Badge>
                            {/* Both facts matter: what staff follow today, and
                                that a replacement is being prepared. */}
                            {policy.current && policy.has_draft && !policy.archived && (
                              <Badge tone="blue">Draft revision</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-5 px-5 pr-6 align-top text-right">
                          <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<Eye className="w-4 h-4" />}
                            onClick={() => onOpen(policy.id)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-[#E3E9F3] dark:border-gray-800 text-[11.5px] text-[#8192AC] dark:text-gray-500">
              {visible.length} {visible.length === 1 ? 'policy' : 'policies'}{query ? ' matching your search' : ''}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

const CategoryButton = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`w-full flex items-center justify-between gap-3 text-left rounded-lg px-2.5 py-2.5 text-[12px] leading-snug transition-colors ${
      active
        ? 'bg-[#EAF1FF] dark:bg-blue-900/25 text-blue-600 dark:text-blue-300 font-semibold'
        : 'text-[#475569] dark:text-gray-300 hover:bg-[#EEF3FA] dark:hover:bg-gray-800'
    }`}
  >
    <span className="min-w-0 truncate" title={label}>{label}</span>
    <span className="text-[11px] tabular-nums opacity-80 shrink-0">{count}</span>
  </button>
);

const emptyMessage = (tab, query, category) => {
  if (query || category !== 'all') return 'Try a different search or category.';
  if (tab === 'drafts') return 'New policies and saved revisions will appear here.';
  if (tab === 'review') return 'There are no policies due for review in the next 30 days.';
  if (tab === 'archived') return 'Archived policies remain available here.';
  return 'Add a policy, or upload a PDF or Word document you already have.';
};

export default PolicyLibrary;
